import { useCallback, useEffect } from 'react';

import type { TConversation } from '@stores/chat-domain.types';

import { useChatStore } from '@stores/chat.store';
import { useChatConversations, useChatMessages } from '@hooks/api/chat-websocket';

import { CHAT_CONVERSATION_TYPES } from '@/utils/constants';
import { insertConversationMessage, resolveActiveTags } from './utils/conversations-update.helpers';

/** Sincroniza novas mensagens e contadores de não-lidas nas listas de conversas (compra/venda). */
export const useConversationsUpdate = (): void => {
	const { fetchConversations } = useChatConversations();
	const { updateConversations } = useChatMessages();
	const conversationActive = useChatStore(s => s.conversation.active);
	const storeConversationToSource = useChatStore(s => s.conversationTo.source);
	const storeConversationFromSource = useChatStore(s => s.conversationFrom.source);
	const storeNewMessage = useChatStore(s => s.newMessage);
	const storeMessageUnread = useChatStore(s => s.messageUnread);
	const activeChatId = conversationActive?.chatId ?? '';

	const getConversationSource = useCallback(
		(chatType: string) =>
			chatType.toLowerCase() === CHAT_CONVERSATION_TYPES.sale
				? storeConversationToSource
				: storeConversationFromSource,
		[storeConversationToSource, storeConversationFromSource],
	);

	useEffect(() => {
		const { fetched, source: messageSource, chatId, itsMyMessage, chatType } = storeNewMessage;
		if (!fetched) return;

		const resolvedChatType = itsMyMessage
			? (chatType ?? '')
			: (messageSource?.[0]?.messageTab ?? '');

		const source = getConversationSource(resolvedChatType);
		const updatedConversations = insertConversationMessage({ source, chatId, messageSource });

		if (!updatedConversations) return;

		if (itsMyMessage || updatedConversations.length === source?.length) {
			updateConversations({ conversations: updatedConversations, chatType: resolvedChatType });
		} else {
			// Refetch precisa respeitar o filtro ativo, senão o backend devolve a lista
			// 'to' inteira (incl. Spam/Blocked) e vaza pro filtro atual. ANCR-5348.
			const { selectedTags } = useChatStore.getState().conversation;
			fetchConversations(resolvedChatType, undefined, resolveActiveTags(resolvedChatType, selectedTags));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeNewMessage]);

	useEffect(() => {
		if (!storeMessageUnread.fetched || activeChatId === storeMessageUnread.source?.chatId) return;

		const { source } = storeMessageUnread;
		const direction = source?.userTabType ?? 'to';

		useChatStore.getState().updateUnreadMessage(direction, {
			chatId: source?.chatId ?? '',
			unreadMessages: source?.unreadMessages ?? 0,
		});

		const allConversations = [...storeConversationToSource, ...storeConversationFromSource];
		const existsInList = allConversations.some(
			(c: TConversation) => c.unreadMessages > 0 && c.chatId === source?.chatId,
		);

		if (!existsInList) {
			// Mesmo cuidado do gatilho de nova mensagem: o refetch tem que respeitar o
			// filtro ativo, senão a conversa spam volta a aparecer na listagem. ANCR-5348.
			const { selectedTags } = useChatStore.getState().conversation;
			fetchConversations(direction, undefined, resolveActiveTags(direction, selectedTags));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeMessageUnread]);
};
